import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { ordersApi } from '../lib/api';
import type {
  Order,
  OrderDetail,
  CreateOrderInput,
  OrderVerificationInput,
  ApiResponse,
} from '../lib/api';
import { useNotifications } from '../stores/uiStore';

export const orderKeys = {
  all: ['orders'] as const,
  lists: () => [...orderKeys.all, 'list'] as const,
  list: (filters: OrderFilters) => [...orderKeys.lists(), filters] as const,
  details: () => [...orderKeys.all, 'detail'] as const,
  detail: (id: string) => [...orderKeys.details(), id] as const,
};

export interface OrderFilters {
  page?: number;
  pageSize?: number;
  status?: string;
  shipId?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export function useOrders(filters: OrderFilters = {}): UseQueryResult<ApiResponse<Order[]>> {
  return useQuery({
    queryKey: orderKeys.list(filters),
    queryFn: () => ordersApi.getAll(filters).then((res) => res.data),
  });
}

export function useOrder(id: string): UseQueryResult<ApiResponse<OrderDetail>> {
  return useQuery({
    queryKey: orderKeys.detail(id),
    queryFn: () => ordersApi.getById(id).then((res) => res.data),
    enabled: !!id,
  });
}

export function useCreateOrder() {
  const queryClient = useQueryClient();
  const { add: addNotification } = useNotifications();

  return useMutation({
    mutationFn: (data: CreateOrderInput) => ordersApi.create(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
      addNotification({
        type: 'success',
        title: 'Order Created',
        message: 'The order has been created successfully.',
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Failed to Create Order',
        message: error.message,
      });
    },
  });
}

export function useUpdateOrderStatus() {
  const queryClient = useQueryClient();
  const { add: addNotification } = useNotifications();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      ordersApi.updateStatus(id, status).then((res) => res.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: orderKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
      addNotification({
        type: 'success',
        title: 'Status Updated',
        message: 'The order status has been updated.',
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Failed to Update Status',
        message: error.message,
      });
    },
  });
}

export function useVerifyOrder() {
  const queryClient = useQueryClient();
  const { add: addNotification } = useNotifications();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: OrderVerificationInput }) =>
      ordersApi.verify(id, data).then((res) => res.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: orderKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
      addNotification({
        type: 'success',
        title: 'Order Verified',
        message: 'The order has been verified and is ready for delivery.',
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Verification Failed',
        message: error.message,
      });
    },
  });
}

export function usePrefetchOrder() {
  const queryClient = useQueryClient();

  return (id: string) => {
    queryClient.prefetchQuery({
      queryKey: orderKeys.detail(id),
      queryFn: () => ordersApi.getById(id).then((res) => res.data),
    });
  };
}

export default {
  useOrders,
  useOrder,
  useCreateOrder,
  useUpdateOrderStatus,
  useVerifyOrder,
  usePrefetchOrder,
};
